/**
 * Rax Contacts Manager Component
 *
 * Unified contact management for cross-channel memory.
 * - List all contacts with their identifiers
 * - Merge duplicate contacts
 * - Edit contact details (name, relationship, notes)
 * - View conversation history across channels
 * - Link/unlink identifiers
 */

import { LitElement, html, css } from "lit";
import { customElement, state } from "lit/decorators.js";
import { raxApi } from "../../api/rax-api.js";
import { raxTheme } from "../styles/rax-theme.js";

// ============================================================================
// Types
// ============================================================================

type IdentifierType =
  | "phone"
  | "email"
  | "telegram"
  | "discord"
  | "slack"
  | "whatsapp"
  | "signal"
  | "matrix"
  | "custom";

type RelationshipType =
  | "friend"
  | "family"
  | "colleague"
  | "acquaintance"
  | "client"
  | "vendor"
  | "other";

interface ContactIdentifier {
  id: string;
  contactId: string;
  type: IdentifierType;
  value: string;
  verified: boolean;
  metadata: Record<string, unknown>;
  createdAt: string;
}

interface Contact {
  id: string;
  userId: string;
  name: string;
  relationship: RelationshipType;
  notes: string | null;
  preferences: Record<string, unknown>;
  identifiers: ContactIdentifier[];
  createdAt: string;
  updatedAt: string;
}

interface CrossChannelMessage {
  id: string;
  channel: string;
  fromId: string;
  fromName: string | null;
  content: string | null;
  receivedAt: string;
  isOutgoing: boolean;
}

interface ContactStats {
  totalContacts: number;
  byRelationship: Record<string, number>;
  totalIdentifiers: number;
  byIdentifierType: Record<string, number>;
}

// ============================================================================
// Component
// ============================================================================

@customElement("rax-contacts-manager")
export class RaxContactsManager extends LitElement {
  static override styles = [
    raxTheme,
    css`
      :host {
        display: block;
      }

      .contacts-container {
        padding: var(--space-6);
      }

      .contacts-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: var(--space-5);
      }

      .contacts-title {
        font-size: var(--font-lg);
        font-weight: 700;
        color: var(--text-primary);
      }

      .header-actions {
        display: flex;
        gap: var(--space-2);
      }

      .action-btn {
        padding: var(--space-2) var(--space-3);
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        color: var(--text-secondary);
        font-size: var(--font-xs);
        cursor: pointer;
        transition: all var(--transition-fast);
      }

      .action-btn:hover {
        border-color: var(--rax-yellow);
        color: var(--rax-yellow);
      }

      .action-btn.primary {
        background: var(--rax-yellow);
        color: var(--rax-black);
        border-color: var(--rax-yellow);
      }

      .action-btn.primary:hover {
        background: var(--rax-gold);
      }

      /* Stats bar */
      .stats-bar {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
        gap: var(--space-4);
        margin-bottom: var(--space-5);
      }

      .stat-card {
        padding: var(--space-4);
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        text-align: center;
      }

      .stat-value {
        font-size: var(--font-2xl);
        font-weight: 700;
        color: var(--rax-yellow);
      }

      .stat-label {
        font-size: var(--font-xs);
        color: var(--text-tertiary);
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin-top: var(--space-1);
      }

      /* Search and filter */
      .controls {
        display: flex;
        gap: var(--space-3);
        margin-bottom: var(--space-4);
        flex-wrap: wrap;
        align-items: center;
      }

      .search-box {
        flex: 1;
        min-width: 250px;
      }

      .search-input {
        width: 100%;
        padding: var(--space-3) var(--space-4);
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        color: var(--text-primary);
        font-size: var(--font-sm);
        outline: none;
        transition: border-color var(--transition-fast);
      }

      .search-input:focus {
        border-color: var(--rax-yellow);
      }

      .search-input::placeholder {
        color: var(--text-tertiary);
      }

      .filter-bar {
        display: flex;
        gap: var(--space-2);
        flex-wrap: wrap;
      }

      .filter-chip {
        padding: var(--space-1) var(--space-3);
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-full);
        color: var(--text-secondary);
        font-size: var(--font-xs);
        cursor: pointer;
        transition: all var(--transition-fast);
      }

      .filter-chip:hover {
        border-color: var(--text-tertiary);
      }

      .filter-chip.active {
        background: var(--rax-yellow);
        color: var(--rax-black);
        border-color: var(--rax-yellow);
        font-weight: 600;
      }

      /* Contacts list */
      .contacts-list {
        display: flex;
        flex-direction: column;
        gap: var(--space-2);
      }

      .contact-card {
        display: grid;
        grid-template-columns: auto 1fr auto auto;
        gap: var(--space-3);
        padding: var(--space-4);
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        align-items: start;
        cursor: pointer;
        transition: all var(--transition-fast);
      }

      .contact-card:hover {
        border-color: var(--text-tertiary);
      }

      .contact-card.selected {
        border-color: var(--rax-yellow);
        background: var(--bg-subtle);
      }

      .contact-avatar {
        width: 48px;
        height: 48px;
        border-radius: var(--radius-full);
        background: var(--bg-primary);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: var(--font-lg);
        font-weight: 600;
        color: var(--rax-yellow);
      }

      .contact-info {
        display: flex;
        flex-direction: column;
        gap: var(--space-1);
      }

      .contact-name {
        font-weight: 600;
        color: var(--text-primary);
        font-size: var(--font-md);
      }

      .contact-relationship {
        font-size: var(--font-xs);
        color: var(--text-secondary);
        padding: 2px 8px;
        background: var(--bg-primary);
        border-radius: var(--radius-sm);
        display: inline-block;
        width: fit-content;
      }

      .contact-identifiers {
        display: flex;
        gap: var(--space-2);
        flex-wrap: wrap;
        margin-top: var(--space-1);
      }

      .identifier-tag {
        display: flex;
        align-items: center;
        gap: var(--space-1);
        padding: 2px 8px;
        background: var(--bg-primary);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-sm);
        font-size: var(--font-xs);
        color: var(--text-secondary);
      }

      .identifier-type {
        color: var(--text-tertiary);
        font-weight: 500;
      }

      .contact-meta {
        text-align: right;
        font-size: var(--font-xs);
        color: var(--text-tertiary);
      }

      .contact-actions {
        display: flex;
        gap: var(--space-1);
        opacity: 0;
        transition: opacity var(--transition-fast);
      }

      .contact-card:hover .contact-actions {
        opacity: 1;
      }

      .contact-action-btn {
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: var(--bg-primary);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-sm);
        color: var(--text-secondary);
        cursor: pointer;
        transition: all var(--transition-fast);
        font-size: 14px;
      }

      .contact-action-btn:hover {
        border-color: var(--rax-yellow);
        color: var(--rax-yellow);
      }

      .contact-action-btn.danger:hover {
        border-color: var(--error);
        color: var(--error);
      }

      /* Detail panel */
      .detail-panel {
        position: fixed;
        top: 0;
        right: 0;
        bottom: 0;
        width: 500px;
        max-width: 90vw;
        background: var(--bg-secondary);
        border-left: 1px solid var(--border-color);
        z-index: 100;
        display: flex;
        flex-direction: column;
        transform: translateX(100%);
        transition: transform var(--transition-normal);
      }

      .detail-panel.open {
        transform: translateX(0);
      }

      .detail-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: var(--space-4);
        border-bottom: 1px solid var(--border-color);
      }

      .detail-title {
        font-size: var(--font-lg);
        font-weight: 600;
        color: var(--text-primary);
      }

      .close-btn {
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: none;
        border: none;
        color: var(--text-secondary);
        cursor: pointer;
        border-radius: var(--radius-sm);
        transition: all var(--transition-fast);
      }

      .close-btn:hover {
        background: var(--bg-hover);
        color: var(--text-primary);
      }

      .detail-content {
        flex: 1;
        overflow-y: auto;
        padding: var(--space-4);
      }

      .detail-section {
        margin-bottom: var(--space-5);
      }

      .section-title {
        font-size: var(--font-sm);
        font-weight: 600;
        color: var(--text-secondary);
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin-bottom: var(--space-3);
      }

      .form-group {
        margin-bottom: var(--space-4);
      }

      .form-label {
        display: block;
        font-size: var(--font-sm);
        font-weight: 500;
        color: var(--text-primary);
        margin-bottom: var(--space-2);
      }

      .form-input,
      .form-select,
      .form-textarea {
        width: 100%;
        padding: var(--space-3);
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        color: var(--text-primary);
        font-size: var(--font-sm);
        outline: none;
        transition: border-color var(--transition-fast);
        box-sizing: border-box;
      }

      .form-select {
        cursor: pointer;
      }

      .form-textarea {
        resize: vertical;
        min-height: 80px;
      }

      .form-input:focus,
      .form-select:focus,
      .form-textarea:focus {
        border-color: var(--rax-yellow);
      }

      /* Identifiers list in detail */
      .identifiers-list {
        display: flex;
        flex-direction: column;
        gap: var(--space-2);
      }

      .identifier-item {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: var(--space-3);
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
      }

      .identifier-info {
        display: flex;
        align-items: center;
        gap: var(--space-2);
      }

      .identifier-icon {
        font-size: var(--font-lg);
      }

      .identifier-details {
        display: flex;
        flex-direction: column;
      }

      .identifier-value {
        font-weight: 500;
        color: var(--text-primary);
      }

      .identifier-type-label {
        font-size: var(--font-xs);
        color: var(--text-tertiary);
      }

      .identifier-remove {
        background: none;
        border: none;
        color: var(--text-tertiary);
        cursor: pointer;
        padding: var(--space-1);
        transition: color var(--transition-fast);
      }

      .identifier-remove:hover {
        color: var(--error);
      }

      .add-identifier-form {
        display: grid;
        grid-template-columns: auto 1fr auto;
        gap: var(--space-2);
        margin-top: var(--space-3);
      }

      .add-identifier-form .form-select {
        width: auto;
      }

      .add-btn {
        padding: var(--space-3) var(--space-4);
        background: var(--rax-yellow);
        color: var(--rax-black);
        border: none;
        border-radius: var(--radius-md);
        font-weight: 600;
        cursor: pointer;
        transition: background var(--transition-fast);
      }

      .add-btn:hover {
        background: var(--rax-gold);
      }

      /* Conversation history */
      .history-list {
        display: flex;
        flex-direction: column;
        gap: var(--space-2);
      }

      .history-item {
        padding: var(--space-3);
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
      }

      .history-item.outgoing {
        background: var(--bg-subtle);
        border-color: var(--rax-yellow);
        border-opacity: 0.3;
      }

      .history-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: var(--space-1);
        font-size: var(--font-xs);
        color: var(--text-tertiary);
      }

      .history-channel {
        padding: 1px 6px;
        background: var(--bg-primary);
        border-radius: var(--radius-sm);
        font-weight: 500;
      }

      .history-content {
        color: var(--text-primary);
        font-size: var(--font-sm);
        line-height: 1.4;
      }

      /* Merge modal */
      .modal-backdrop {
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.7);
        z-index: 200;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: var(--space-4);
      }

      .modal {
        background: var(--bg-secondary);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-lg);
        padding: var(--space-6);
        max-width: 500px;
        width: 100%;
        max-height: 80vh;
        overflow-y: auto;
      }

      .modal-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: var(--space-5);
      }

      .modal-title {
        font-size: var(--font-lg);
        font-weight: 600;
        color: var(--text-primary);
      }

      .modal-actions {
        display: flex;
        justify-content: flex-end;
        gap: var(--space-3);
        margin-top: var(--space-5);
      }

      .btn {
        padding: var(--space-3) var(--space-4);
        border: none;
        border-radius: var(--radius-md);
        font-size: var(--font-sm);
        font-weight: 600;
        cursor: pointer;
        transition: all var(--transition-fast);
      }

      .btn-primary {
        background: var(--rax-yellow);
        color: var(--rax-black);
      }

      .btn-primary:hover {
        background: var(--rax-gold);
      }

      .btn-secondary {
        background: var(--bg-elevated);
        color: var(--text-secondary);
        border: 1px solid var(--border-color);
      }

      .btn-secondary:hover {
        border-color: var(--text-tertiary);
        color: var(--text-primary);
      }

      /* Loading and empty states */
      .loading,
      .empty-state {
        text-align: center;
        padding: var(--space-8);
        color: var(--text-tertiary);
      }

      .merge-candidates {
        display: flex;
        flex-direction: column;
        gap: var(--space-2);
      }

      .merge-candidate {
        display: flex;
        align-items: center;
        gap: var(--space-3);
        padding: var(--space-3);
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        cursor: pointer;
        transition: all var(--transition-fast);
      }

      .merge-candidate:hover {
        border-color: var(--text-tertiary);
      }

      .merge-candidate.selected {
        border-color: var(--rax-yellow);
        background: var(--bg-subtle);
      }

      .merge-candidate-radio {
        width: 20px;
        height: 20px;
        border: 2px solid var(--border-color);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .merge-candidate.selected .merge-candidate-radio {
        border-color: var(--rax-yellow);
      }

      .merge-candidate.selected .merge-candidate-radio::after {
        content: "";
        width: 10px;
        height: 10px;
        background: var(--rax-yellow);
        border-radius: 50%;
      }

      .merge-candidate-info {
        flex: 1;
      }

      .merge-candidate-name {
        font-weight: 500;
        color: var(--text-primary);
      }

      .merge-candidate-identifiers {
        font-size: var(--font-xs);
        color: var(--text-tertiary);
      }
    `,
  ];

  @state()
  private contacts: Contact[] = [];

  @state()
  private stats: ContactStats | null = null;

  @state()
  private loading = true;

  @state()
  private searchQuery = "";

  @state()
  private relationshipFilter: RelationshipType | null = null;

  @state()
  private selectedContact: Contact | null = null;

  @state()
  private editingContact: Contact | null = null;

  @state()
  private showMergeModal = false;

  @state()
  private mergeSource: Contact | null = null;

  @state()
  private mergeTarget: Contact | null = null;

  @state()
  private conversationHistory: CrossChannelMessage[] = [];

  @state()
  private historyLoading = false;

  @state()
  private newIdentifierType: IdentifierType = "phone";

  @state()
  private newIdentifierValue = "";

  override async connectedCallback() {
    super.connectedCallback();
    await this.loadContacts();
    await this.loadStats();
  }

  private async loadContacts() {
    this.loading = true;
    try {
      const res = await raxApi.request<Contact[]>("GET", "/api/rax/contacts");
      if (res.success && res.data) {
        this.contacts = res.data;
      }
    } catch (err) {
      console.error("[ContactsManager] Failed to load contacts:", err);
    } finally {
      this.loading = false;
    }
  }

  private async loadStats() {
    try {
      const res = await raxApi.request<ContactStats>("GET", "/api/rax/contacts/stats");
      if (res.success && res.data) {
        this.stats = res.data;
      }
    } catch (err) {
      console.error("[ContactsManager] Failed to load stats:", err);
    }
  }

  private async loadConversationHistory(contact: Contact) {
    this.historyLoading = true;
    try {
      const res = await raxApi.request<CrossChannelMessage[]>(
        "GET",
        `/api/rax/contacts/${contact.id}/history`,
      );
      if (res.success && res.data) {
        this.conversationHistory = res.data;
      }
    } catch (err) {
      console.error("[ContactsManager] Failed to load history:", err);
    } finally {
      this.historyLoading = false;
    }
  }

  private get filteredContacts(): Contact[] {
    let filtered = this.contacts;

    if (this.relationshipFilter) {
      filtered = filtered.filter((c) => c.relationship === this.relationshipFilter);
    }

    if (this.searchQuery) {
      const query = this.searchQuery.toLowerCase();
      filtered = filtered.filter(
        (c) =>
          c.name.toLowerCase().includes(query) ||
          c.identifiers.some((id) => id.value.toLowerCase().includes(query)),
      );
    }

    return filtered.sort((a, b) => a.name.localeCompare(b.name));
  }

  private getInitials(name: string): string {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  }

  private getIdentifierIcon(type: IdentifierType): string {
    const icons: Record<IdentifierType, string> = {
      phone: "phone",
      email: "mail",
      telegram: "send",
      discord: "hash",
      slack: "message-square",
      whatsapp: "message-circle",
      signal: "shield",
      matrix: "grid",
      custom: "user",
    };
    return icons[type] || "user";
  }

  private formatDate(dateStr: string): string {
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }

  private formatDateTime(dateStr: string): string {
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  private handleSearch(e: Event) {
    this.searchQuery = (e.target as HTMLInputElement).value;
  }

  private setRelationshipFilter(relationship: RelationshipType | null) {
    this.relationshipFilter = this.relationshipFilter === relationship ? null : relationship;
  }

  private async selectContact(contact: Contact) {
    this.selectedContact = contact;
    this.editingContact = { ...contact };
    await this.loadConversationHistory(contact);
  }

  private closeDetailPanel() {
    this.selectedContact = null;
    this.editingContact = null;
    this.conversationHistory = [];
  }

  private handleEditChange(field: keyof Contact, value: string) {
    if (this.editingContact) {
      this.editingContact = { ...this.editingContact, [field]: value };
    }
  }

  private async saveContact() {
    if (!this.editingContact) return;

    try {
      const res = await raxApi.request("PATCH", `/api/rax/contacts/${this.editingContact.id}`, {
        name: this.editingContact.name,
        relationship: this.editingContact.relationship,
        notes: this.editingContact.notes,
      });

      if (res.success) {
        await this.loadContacts();
        this.closeDetailPanel();
      }
    } catch (err) {
      console.error("[ContactsManager] Failed to save contact:", err);
    }
  }

  private async deleteContact(contact: Contact) {
    if (!confirm(`Delete "${contact.name}"? This will also remove all linked identifiers.`)) {
      return;
    }

    try {
      const res = await raxApi.request("DELETE", `/api/rax/contacts/${contact.id}`);
      if (res.success) {
        await this.loadContacts();
        await this.loadStats();
        if (this.selectedContact?.id === contact.id) {
          this.closeDetailPanel();
        }
      }
    } catch (err) {
      console.error("[ContactsManager] Failed to delete contact:", err);
    }
  }

  private async addIdentifier() {
    if (!this.editingContact || !this.newIdentifierValue.trim()) return;

    try {
      const res = await raxApi.request(
        "POST",
        `/api/rax/contacts/${this.editingContact.id}/identifiers`,
        {
          type: this.newIdentifierType,
          value: this.newIdentifierValue.trim(),
        },
      );

      if (res.success) {
        this.newIdentifierValue = "";
        await this.loadContacts();
        // Update the selected contact with new data
        const updated = this.contacts.find((c) => c.id === this.editingContact?.id);
        if (updated) {
          this.selectedContact = updated;
          this.editingContact = { ...updated };
        }
      }
    } catch (err) {
      console.error("[ContactsManager] Failed to add identifier:", err);
    }
  }

  private async removeIdentifier(identifierId: string) {
    if (!this.editingContact) return;

    try {
      const res = await raxApi.request(
        "DELETE",
        `/api/rax/contacts/${this.editingContact.id}/identifiers/${identifierId}`,
      );

      if (res.success) {
        await this.loadContacts();
        const updated = this.contacts.find((c) => c.id === this.editingContact?.id);
        if (updated) {
          this.selectedContact = updated;
          this.editingContact = { ...updated };
        }
      }
    } catch (err) {
      console.error("[ContactsManager] Failed to remove identifier:", err);
    }
  }

  private openMergeModal(contact: Contact) {
    this.mergeSource = contact;
    this.mergeTarget = null;
    this.showMergeModal = true;
  }

  private closeMergeModal() {
    this.showMergeModal = false;
    this.mergeSource = null;
    this.mergeTarget = null;
  }

  private async performMerge() {
    if (!this.mergeSource || !this.mergeTarget) return;

    try {
      const res = await raxApi.request(
        "POST",
        `/api/rax/contacts/${this.mergeTarget.id}/merge`,
        { sourceId: this.mergeSource.id },
      );

      if (res.success) {
        await this.loadContacts();
        await this.loadStats();
        this.closeMergeModal();
        if (this.selectedContact?.id === this.mergeSource.id) {
          this.closeDetailPanel();
        }
      }
    } catch (err) {
      console.error("[ContactsManager] Failed to merge contacts:", err);
    }
  }

  private async createContact() {
    const name = prompt("Enter contact name:");
    if (!name?.trim()) return;

    try {
      const res = await raxApi.request<Contact>("POST", "/api/rax/contacts", {
        name: name.trim(),
        relationship: "other",
      });

      if (res.success && res.data) {
        await this.loadContacts();
        await this.loadStats();
        this.selectContact(res.data);
      }
    } catch (err) {
      console.error("[ContactsManager] Failed to create contact:", err);
    }
  }

  override render() {
    if (this.loading) {
      return html`<div class="loading">Loading contacts...</div>`;
    }

    const relationships: RelationshipType[] = [
      "friend",
      "family",
      "colleague",
      "acquaintance",
      "client",
      "vendor",
      "other",
    ];

    return html`
      <div class="contacts-container">
        <div class="contacts-header">
          <div class="contacts-title">Unified Contacts</div>
          <div class="header-actions">
            <button class="action-btn primary" @click=${this.createContact}>+ New Contact</button>
            <button class="action-btn" @click=${this.loadContacts}>Refresh</button>
          </div>
        </div>

        ${this.stats
          ? html`
              <div class="stats-bar">
                <div class="stat-card">
                  <div class="stat-value">${this.stats.totalContacts}</div>
                  <div class="stat-label">Contacts</div>
                </div>
                <div class="stat-card">
                  <div class="stat-value">${this.stats.totalIdentifiers}</div>
                  <div class="stat-label">Identifiers</div>
                </div>
                <div class="stat-card">
                  <div class="stat-value">
                    ${Object.keys(this.stats.byIdentifierType).length}
                  </div>
                  <div class="stat-label">Channels</div>
                </div>
              </div>
            `
          : ""}

        <div class="controls">
          <div class="search-box">
            <input
              class="search-input"
              type="text"
              placeholder="Search contacts..."
              .value=${this.searchQuery}
              @input=${this.handleSearch}
            />
          </div>

          <div class="filter-bar">
            <span
              class="filter-chip ${!this.relationshipFilter ? "active" : ""}"
              @click=${() => this.setRelationshipFilter(null)}
            >
              All
            </span>
            ${relationships.map(
              (rel) => html`
                <span
                  class="filter-chip ${this.relationshipFilter === rel ? "active" : ""}"
                  @click=${() => this.setRelationshipFilter(rel)}
                >
                  ${rel}
                </span>
              `,
            )}
          </div>
        </div>

        ${this.filteredContacts.length > 0
          ? html`
              <div class="contacts-list">
                ${this.filteredContacts.map(
                  (contact) => html`
                    <div
                      class="contact-card ${this.selectedContact?.id === contact.id
                        ? "selected"
                        : ""}"
                      @click=${() => this.selectContact(contact)}
                    >
                      <div class="contact-avatar">${this.getInitials(contact.name)}</div>
                      <div class="contact-info">
                        <div class="contact-name">${contact.name}</div>
                        <div class="contact-relationship">${contact.relationship}</div>
                        <div class="contact-identifiers">
                          ${contact.identifiers.slice(0, 3).map(
                            (id) => html`
                              <span class="identifier-tag">
                                <span class="identifier-type">${id.type}:</span>
                                ${id.value}
                              </span>
                            `,
                          )}
                          ${contact.identifiers.length > 3
                            ? html`
                                <span class="identifier-tag">
                                  +${contact.identifiers.length - 3} more
                                </span>
                              `
                            : ""}
                        </div>
                      </div>
                      <div class="contact-meta">
                        <div>Updated ${this.formatDate(contact.updatedAt)}</div>
                      </div>
                      <div class="contact-actions">
                        <button
                          class="contact-action-btn"
                          title="Merge"
                          @click=${(e: Event) => {
                            e.stopPropagation();
                            this.openMergeModal(contact);
                          }}
                        >
                          M
                        </button>
                        <button
                          class="contact-action-btn danger"
                          title="Delete"
                          @click=${(e: Event) => {
                            e.stopPropagation();
                            this.deleteContact(contact);
                          }}
                        >
                          X
                        </button>
                      </div>
                    </div>
                  `,
                )}
              </div>
            `
          : html`
              <div class="empty-state">
                <p>No contacts found.</p>
                <p>Contacts are automatically created when you receive messages.</p>
              </div>
            `}
      </div>

      <!-- Detail Panel -->
      <div class="detail-panel ${this.selectedContact ? "open" : ""}">
        ${this.editingContact
          ? html`
              <div class="detail-header">
                <div class="detail-title">Edit Contact</div>
                <button class="close-btn" @click=${this.closeDetailPanel}>X</button>
              </div>
              <div class="detail-content">
                <div class="detail-section">
                  <div class="section-title">Basic Info</div>
                  <div class="form-group">
                    <label class="form-label">Name</label>
                    <input
                      class="form-input"
                      type="text"
                      .value=${this.editingContact.name}
                      @input=${(e: Event) =>
                        this.handleEditChange("name", (e.target as HTMLInputElement).value)}
                    />
                  </div>
                  <div class="form-group">
                    <label class="form-label">Relationship</label>
                    <select
                      class="form-select"
                      .value=${this.editingContact.relationship}
                      @change=${(e: Event) =>
                        this.handleEditChange(
                          "relationship",
                          (e.target as HTMLSelectElement).value,
                        )}
                    >
                      <option value="friend">Friend</option>
                      <option value="family">Family</option>
                      <option value="colleague">Colleague</option>
                      <option value="acquaintance">Acquaintance</option>
                      <option value="client">Client</option>
                      <option value="vendor">Vendor</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div class="form-group">
                    <label class="form-label">Notes</label>
                    <textarea
                      class="form-textarea"
                      .value=${this.editingContact.notes || ""}
                      @input=${(e: Event) =>
                        this.handleEditChange("notes", (e.target as HTMLTextAreaElement).value)}
                    ></textarea>
                  </div>
                  <button class="btn btn-primary" @click=${this.saveContact}>Save Changes</button>
                </div>

                <div class="detail-section">
                  <div class="section-title">Identifiers</div>
                  <div class="identifiers-list">
                    ${this.editingContact.identifiers.map(
                      (id) => html`
                        <div class="identifier-item">
                          <div class="identifier-info">
                            <span class="identifier-icon">${this.getIdentifierIcon(id.type)}</span>
                            <div class="identifier-details">
                              <span class="identifier-value">${id.value}</span>
                              <span class="identifier-type-label">${id.type}</span>
                            </div>
                          </div>
                          <button
                            class="identifier-remove"
                            @click=${() => this.removeIdentifier(id.id)}
                          >
                            X
                          </button>
                        </div>
                      `,
                    )}
                  </div>
                  <div class="add-identifier-form">
                    <select
                      class="form-select"
                      .value=${this.newIdentifierType}
                      @change=${(e: Event) =>
                        (this.newIdentifierType = (e.target as HTMLSelectElement)
                          .value as IdentifierType)}
                    >
                      <option value="phone">Phone</option>
                      <option value="email">Email</option>
                      <option value="telegram">Telegram</option>
                      <option value="discord">Discord</option>
                      <option value="slack">Slack</option>
                      <option value="whatsapp">WhatsApp</option>
                      <option value="signal">Signal</option>
                      <option value="matrix">Matrix</option>
                      <option value="custom">Custom</option>
                    </select>
                    <input
                      class="form-input"
                      type="text"
                      placeholder="Enter identifier..."
                      .value=${this.newIdentifierValue}
                      @input=${(e: Event) =>
                        (this.newIdentifierValue = (e.target as HTMLInputElement).value)}
                    />
                    <button class="add-btn" @click=${this.addIdentifier}>Add</button>
                  </div>
                </div>

                <div class="detail-section">
                  <div class="section-title">Conversation History</div>
                  ${this.historyLoading
                    ? html`<div class="loading">Loading history...</div>`
                    : this.conversationHistory.length > 0
                      ? html`
                          <div class="history-list">
                            ${this.conversationHistory.map(
                              (msg) => html`
                                <div class="history-item ${msg.isOutgoing ? "outgoing" : ""}">
                                  <div class="history-header">
                                    <span class="history-channel">${msg.channel}</span>
                                    <span>${this.formatDateTime(msg.receivedAt)}</span>
                                  </div>
                                  <div class="history-content">
                                    ${msg.isOutgoing ? "You: " : ""}${msg.content || "[no text]"}
                                  </div>
                                </div>
                              `,
                            )}
                          </div>
                        `
                      : html`<div class="empty-state">No conversation history yet.</div>`}
                </div>
              </div>
            `
          : ""}
      </div>

      <!-- Merge Modal -->
      ${this.showMergeModal
        ? html`
            <div
              class="modal-backdrop"
              @click=${(e: Event) => e.target === e.currentTarget && this.closeMergeModal()}
            >
              <div class="modal">
                <div class="modal-header">
                  <div class="modal-title">Merge Contact</div>
                  <button class="close-btn" @click=${this.closeMergeModal}>X</button>
                </div>

                <p>
                  Merge "${this.mergeSource?.name}" into another contact. All identifiers will be
                  moved to the target.
                </p>

                <div class="section-title" style="margin-top: var(--space-4);">
                  Select Target Contact
                </div>
                <div class="merge-candidates">
                  ${this.contacts
                    .filter((c) => c.id !== this.mergeSource?.id)
                    .map(
                      (c) => html`
                        <div
                          class="merge-candidate ${this.mergeTarget?.id === c.id ? "selected" : ""}"
                          @click=${() => (this.mergeTarget = c)}
                        >
                          <div class="merge-candidate-radio"></div>
                          <div class="merge-candidate-info">
                            <div class="merge-candidate-name">${c.name}</div>
                            <div class="merge-candidate-identifiers">
                              ${c.identifiers.map((id) => id.value).join(", ") || "No identifiers"}
                            </div>
                          </div>
                        </div>
                      `,
                    )}
                </div>

                <div class="modal-actions">
                  <button class="btn btn-secondary" @click=${this.closeMergeModal}>Cancel</button>
                  <button
                    class="btn btn-primary"
                    ?disabled=${!this.mergeTarget}
                    @click=${this.performMerge}
                  >
                    Merge Contacts
                  </button>
                </div>
              </div>
            </div>
          `
        : ""}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rax-contacts-manager": RaxContactsManager;
  }
}
